import { useState, useEffect, useCallback, useRef } from 'react'
import { isPWAStandalone } from '../utils/links'

const DISMISS_KEY = 'pwa-install-dismissed'
const ENGAGEMENT_DELAY_MS = 30_000 // 30 seconds

/**
 * Reads the dismiss record from localStorage.
 * Shape: { permanent?: boolean, until?: number }
 */
function getDismissRecord() {
    try {
        return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}')
    } catch {
        return {}
    }
}

function isDismissed() {
    const record = getDismissRecord()
    if (record.permanent) return true
    if (record.until && Date.now() < record.until) return true
    return false
}

// ─── Platform detection ───────────────────────────────────────────────────

function getIsIOS() {
    if (typeof navigator === 'undefined') return false
    return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream
}

function getIsIOSSafari() {
    if (!getIsIOS()) return false
    return /safari/i.test(navigator.userAgent) &&
           !/chrome|crios|fxios/i.test(navigator.userAgent)
}

// ─── Hook ─────────────────────────────────────────────────────────────────

export default function useInstallPrompt() {
    const deferredPrompt = useRef(null)
    const [showBanner, setShowBanner] = useState(false)
    const [showIOSModal, setShowIOSModal] = useState(false)
    const [engaged, setEngaged] = useState(false)

    // Track whether beforeinstallprompt fired (Chromium browsers)
    const [promptAvailable, setPromptAvailable] = useState(false)

    const isIOS = getIsIOS()
    const isIOSSafari = getIsIOSSafari()

    // ── Engagement delay (30 s) ───────────────────────────────────────────
    useEffect(() => {
        if (isPWAStandalone || isDismissed()) return

        const timer = setTimeout(() => setEngaged(true), ENGAGEMENT_DELAY_MS)
        return () => clearTimeout(timer)
    }, [])

    // ── Capture beforeinstallprompt (Chromium) ────────────────────────────
    useEffect(() => {
        if (isPWAStandalone) return

        const handler = (e) => {
            e.preventDefault()
            deferredPrompt.current = e
            setPromptAvailable(true)
        }

        window.addEventListener('beforeinstallprompt', handler)
        return () => window.removeEventListener('beforeinstallprompt', handler)
    }, [])

    // ── Listen for appinstalled → permanent dismiss ───────────────────────
    useEffect(() => {
        const handler = () => {
            localStorage.setItem(DISMISS_KEY, JSON.stringify({ permanent: true }))
            setShowBanner(false)
            setShowIOSModal(false)
        }

        window.addEventListener('appinstalled', handler)
        return () => window.removeEventListener('appinstalled', handler)
    }, [])

    // ── Show banner/modal once engaged + not dismissed ────────────────────
    useEffect(() => {
        if (!engaged || isPWAStandalone || isDismissed()) return

        if (promptAvailable) {
            setShowBanner(true)
        } else if (isIOSSafari) {
            setShowIOSModal(true)
        }
    }, [engaged, promptAvailable, isIOSSafari])

    // ── Actions ───────────────────────────────────────────────────────────

    const triggerInstall = useCallback(async () => {
        if (!deferredPrompt.current) return

        deferredPrompt.current.prompt()
        const { outcome } = await deferredPrompt.current.userChoice

        if (outcome === 'accepted') {
            localStorage.setItem(DISMISS_KEY, JSON.stringify({ permanent: true }))
        }

        deferredPrompt.current = null
        setShowBanner(false)
    }, [])

    const dismiss = useCallback(() => {
        // Snooze for 7 days
        localStorage.setItem(DISMISS_KEY, JSON.stringify({
            until: Date.now() + 7 * 24 * 60 * 60 * 1000
        }))
        setShowBanner(false)
        setShowIOSModal(false)
    }, [])

    const dismissPermanent = useCallback(() => {
        localStorage.setItem(DISMISS_KEY, JSON.stringify({ permanent: true }))
        setShowBanner(false)
        setShowIOSModal(false)
    }, [])

    return {
        showBanner,
        showIOSModal,
        triggerInstall,
        dismiss,
        dismissPermanent,
    }
}
